import React, {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
} from "react";

import { type ApolloError, gql } from "@apollo/client";
import { useUser } from "@components/user-context";
import {
  type FetchUserDetails_me,
  type FetchUserDetails,
} from "@north-beam/nb-client-side";
import { type AttributionWindow } from "@north-beam/nb-common/constants";

import { useNorthbeamQuery } from "./use-northbeam-query";

export type ContextType = {
  loading: boolean;
  error: ApolloError | undefined;
  userEnrichment?: FetchUserDetails_me;
};
const UserDetailsContext = createContext<ContextType>({
  featureFlags: undefined,
  loading: false,
  error: undefined,
} as ContextType);

export function UserDetailsProvider({ children }: PropsWithChildren<unknown>) {
  const { data, loading, error } = useNorthbeamQuery<FetchUserDetails>(
    FETCH_USER_DETAILS,
    {},
  );
  const { user } = useUser();

  useEffect(() => {
    if (data?.me.attributionModels) {
      user.updateDisplayUserResponse({
        attributionModels: data?.me.attributionModels,
        isPixelConfigurationSubaccount: data.me.isPixelConfigurationSubaccount,
      });
    }
  }, [data?.me, user]);

  return (
    <UserDetailsContext.Provider
      value={{ loading, error, userEnrichment: data?.me }}
    >
      {children}
    </UserDetailsContext.Provider>
  );
}

export const useUserEnrichment = () => {
  const context = useContext(UserDetailsContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider",
    );
  }
  const { userEnrichment, loading, error } = context;

  return { userEnrichment, loading, error };
};

export const FETCH_USER_DETAILS = gql`
  query FetchUserDetails {
    me {
      attributionModels
      isPixelConfigurationSubaccount
      attribution {
        attributionModelOptions {
          always_on
          state
          admin_only
          attribution_model_label
          attribution_model_name
        }
        attributionWindowDaysOptions {
          always_on
          state
          attribution_window_days
          attribution_window_days_label
        }
      }
    }
  }
`;
